import React, { useState, useEffect } from 'react';
import { Search, Plus, Edit, Trash2, Phone, Mail, FileText, User, ExternalLink, Download, AlertCircle, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { StatCard } from '../../components/ui/StatCard';
import { OwnerService } from '../../handling_data/services/OwnerService';
import { OwnerFormModal } from './OwnerFormModal';
import { Owner } from '../../handling_data/models/Owner';

export const OwnersList: React.FC = () => {
    const { t, i18n } = useTranslation();
    const [searchTerm, setSearchTerm] = useState('');
    const [owners, setOwners] = useState<Owner[]>([]);
    const [loading, setLoading] = useState(true);

    // Add/Edit Owner Modal State
    const [isFormModalOpen, setIsFormModalOpen] = useState(false);
    const [ownerToEdit, setOwnerToEdit] = useState<Owner | null>(null);

    // Delete Modal State
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [selectedOwner, setSelectedOwner] = useState<Owner | null>(null);
    const [deleteError, setDeleteError] = useState<string | null>(null);

    // Certificates Modal State
    const [isCertModalOpen, setIsCertModalOpen] = useState(false);
    const [selectedCertificates, setSelectedCertificates] = useState<string[]>([]);
    const [selectedOwnerName, setSelectedOwnerName] = useState('');

    useEffect(() => {
        fetchOwners();
    }, []);

    const fetchOwners = async () => {
        try {
            setLoading(true);
            const response = await OwnerService.getOwners();
            if (response.status === 'success') {
                const data = response.data;
                const ownersList = Array.isArray(data) ? data : data.data;
                setOwners(ownersList || []);
            }
        } catch (error) {
            console.error('Failed to fetch owners:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteOwner = async () => {
        if (!selectedOwner) return;

        try {
            setLoading(true);
            setDeleteError(null);
            const response = await OwnerService.deleteOwner(selectedOwner.id);
            if (response.status === 'success') {
                setIsDeleteModalOpen(false);
                fetchOwners();
            }
        } catch (err: any) {
            console.error('Failed to delete owner:', err);
            if (err.response?.status === 404) {
                setDeleteError(t('owners_page.user_not_found'));
            } else {
                setDeleteError(err.response?.data?.message || 'فشل حذف المالك');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleViewCertificates = (owner: Owner) => {
        const certs = owner.profile?.certificates_urls || owner.profile?.certificates;
        if (certs) {
            const certArray = Array.isArray(certs) ? certs : Object.values(certs);
            setSelectedCertificates(certArray.filter(c => typeof c === 'string') as string[]);
            setSelectedOwnerName(owner.name);
            setIsCertModalOpen(true);
        }
    };

    const filteredOwners = owners.filter(owner => {
        const search = searchTerm.toLowerCase();
        const nameMatch = (owner.name || '').toLowerCase().includes(search);
        const emailMatch = (owner.email || '').toLowerCase().includes(search);
        const officialNameMatch = (owner.profile?.official_name || '').toLowerCase().includes(search);
        return nameMatch || emailMatch || officialNameMatch;
    });

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Page Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-primary">{t('owners_page.title')}</h2>
                    <p className="text-v3-text-secondary text-sm">{t('owners_page.subtitle')}</p>
                </div>
                <Button
                    onClick={() => {
                        setOwnerToEdit(null);
                        setIsFormModalOpen(true);
                    }}
                    className="flex items-center gap-2 bg-primary text-white px-5 py-2.5 rounded-xl hover:bg-primary/90 transition-all shadow-lg shadow-primary/20"
                >
                    <Plus className="w-5 h-5" />
                    <span>{t('owners_page.add_new')}</span>
                </Button>
            </div>

            {/* Stats Row */}
            {!loading && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <StatCard
                        title={t('sidebar.owners')}
                        value={owners.length}
                        icon={Users}
                        color="bg-purple-500"
                    />
                </div>
            )}

            {/* Search */}
            <div className="bg-white p-4 rounded-2xl shadow-sm border border-v3-border">
                <div className="relative">
                    <Search className={`absolute ${i18n.dir() === 'rtl' ? 'right-3' : 'left-3'} top-3 w-5 h-5 text-gray-400`} />
                    <input
                        type="text"
                        placeholder={t('owners_page.search_placeholder')}
                        className={`w-full ${i18n.dir() === 'rtl' ? 'pr-10 pl-4' : 'pl-10 pr-4'} py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all`}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            {/* Owners Table */}
            <div className="bg-white rounded-2xl shadow-sm border border-v3-border overflow-hidden min-h-[400px] flex flex-col">
                {loading ? (
                    <div className="flex-1 flex flex-col items-center justify-center gap-3 text-gray-400">
                        <div className="w-8 h-8 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
                        <span>{t('common.loading')}...</span>
                    </div>
                ) : filteredOwners.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="w-full text-right">
                            <thead className="bg-gray-50 border-b border-gray-100">
                                <tr>
                                    <th className={`py-4 px-6 font-semibold text-gray-600 text-sm ${i18n.dir() === 'rtl' ? 'text-right' : 'text-left'}`}>{t('owners_page.table.owner')}</th>
                                    <th className={`py-4 px-6 font-semibold text-gray-600 text-sm ${i18n.dir() === 'rtl' ? 'text-right' : 'text-left'}`}>{t('owners_page.table.contact')}</th>
                                    <th className="py-4 px-6 font-semibold text-gray-600 text-sm text-center">{t('owners_page.table.identity')}</th>
                                    <th className="py-4 px-6 font-semibold text-gray-600 text-sm text-center">{t('common.actions')}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {filteredOwners.map((owner) => (
                                    <tr key={owner.id} className="hover:bg-gray-50/50 transition-colors group">
                                        <td className="py-4 px-6">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-primary/5 flex items-center justify-center border border-gray-100 text-primary">
                                                    <User className="w-6 h-6" />
                                                </div>
                                                <div className={i18n.dir() === 'rtl' ? 'text-right' : 'text-left'}>
                                                    <div className="font-bold text-gray-800">{owner.profile?.official_name || owner.name || '---'}</div>
                                                    <div className="text-[10px] text-gray-400 mt-0.5">
                                                        ID: #{owner.id} {owner.member_since && ` • ${t('common.member_since')}: ${owner.member_since}`}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="py-4 px-6">
                                            <div className="flex flex-col gap-1">
                                                <div className={`flex items-center gap-2 text-sm text-gray-600 ${i18n.dir() === 'rtl' ? 'flex-row-reverse justify-end' : ''}`}>
                                                    <Mail className="w-4 h-4 text-primary/60" />
                                                    <a href={`mailto:${owner.email}`} className="hover:text-primary transition-colors">{owner.email || '---'}</a>
                                                </div>
                                                {(owner.phone || owner.profile?.contact_phone) && (
                                                    <div className={`flex items-center gap-2 text-sm text-gray-600 ${i18n.dir() === 'rtl' ? 'flex-row-reverse justify-end' : ''}`}>
                                                        <Phone className="w-4 h-4 text-primary/60" />
                                                        <span className="dir-ltr">{owner.profile?.contact_phone || owner.phone}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td className="py-4 px-6 text-center">
                                            <div className="flex flex-col items-center gap-2">
                                                {/* Identity Proof Link */}
                                                {(() => {
                                                    const identity = owner.profile?.identity_url || owner.profile?.documents?.identity || owner.profile?.identity_proof;
                                                    if (!identity) return null;
                                                    const url = identity.startsWith('http') ? identity : `https://api.istanbulviillas.com/storage/${identity}`;
                                                    return (
                                                        <a
                                                            href={url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-50 text-green-700 hover:bg-green-100 transition-colors border border-green-100 text-xs font-medium"
                                                        >
                                                            <FileText className="w-3.5 h-3.5" />
                                                            <span>{t('owners_page.view_identity')}</span>
                                                        </a>
                                                    );
                                                })()}

                                                {/* Certificates Button */}
                                                {(owner.profile?.certificates_urls?.length || 0) > 0 || (owner.profile?.certificates && Object.keys(owner.profile.certificates).length > 0) ? (
                                                    <button
                                                        onClick={() => handleViewCertificates(owner)}
                                                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors border border-blue-100 text-xs font-medium"
                                                    >
                                                        <FileText className="w-3.5 h-3.5" />
                                                        <span>{t('common.view_certificates')} ({Array.isArray(owner.profile?.certificates_urls || owner.profile?.certificates) ? (owner.profile?.certificates_urls || owner.profile?.certificates as any[]).length : Object.keys(owner.profile?.certificates || {}).length})</span>
                                                    </button>
                                                ) : null}

                                                {!(owner.profile?.identity_url || owner.profile?.documents?.identity || owner.profile?.identity_proof || owner.profile?.certificates_urls || owner.profile?.certificates) && (
                                                    <span className="text-xs text-gray-400 italic">{t('common.no_data')}</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="py-4 px-6">
                                            <div className="flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <button
                                                    onClick={() => {
                                                        setOwnerToEdit(owner);
                                                        setIsFormModalOpen(true);
                                                    }}
                                                    className="w-8 h-8 flex items-center justify-center hover:bg-blue-50 text-blue-600 rounded-lg transition-colors border border-transparent hover:border-blue-100"
                                                    title={t('common.edit')}
                                                >
                                                    <Edit className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setSelectedOwner(owner);
                                                        setIsDeleteModalOpen(true);
                                                        setDeleteConfirmText('');
                                                        setDeleteError(null);
                                                    }}
                                                    className="w-8 h-8 flex items-center justify-center hover:bg-red-50 text-red-600 rounded-lg transition-colors border border-transparent hover:border-red-100"
                                                    title={t('common.delete')}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="flex-1 flex flex-col items-center justify-center text-gray-400 py-20">
                        <Search className="w-12 h-12 mb-4 opacity-20" />
                        <p>{t('owners_page.no_data') || 'لا يوجد ملاك لعرضهم حالياً'}</p>
                    </div>
                )}
            </div>

            {/* Delete Confirmation Modal */}
            <Modal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                title={t('common.confirm_delete')}
            >
                <div className="space-y-4">
                    {deleteError && (
                        <div className="p-3 rounded-xl bg-red-50 border border-red-100 text-red-600 text-sm flex items-center gap-2">
                            <AlertCircle className="w-4 h-4" />
                            <span>{deleteError}</span>
                        </div>
                    )}
                    <div className="p-4 rounded-xl bg-red-50 border border-red-100 flex gap-3">
                        <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-red-700 leading-relaxed font-medium">
                            {t('owners_page.delete_warning')}
                        </p>
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm text-gray-600">
                            {t('owners_page.delete_confirm_instruction', { word: 'delete' })}
                        </label>
                        <input
                            type="text"
                            className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all text-center font-bold tracking-widest uppercase"
                            value={deleteConfirmText}
                            onChange={(e) => setDeleteConfirmText(e.target.value)}
                            placeholder="DELETE"
                        />
                    </div>

                    <div className="flex gap-3 pt-2">
                        <Button
                            variant="outline"
                            onClick={() => setIsDeleteModalOpen(false)}
                            className="flex-1"
                        >
                            {t('common.cancel')}
                        </Button>
                        <Button
                            onClick={handleDeleteOwner}
                            disabled={deleteConfirmText.toLowerCase() !== 'delete' || loading}
                            className="flex-1 bg-red-600 text-white hover:bg-red-700 shadow-lg shadow-red-200 border-none"
                        >
                            {loading ? t('common.loading') : t('common.delete')}
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Certificates Modal */}
            <Modal
                isOpen={isCertModalOpen}
                onClose={() => setIsCertModalOpen(false)}
                title={`${t('common.certificates')} - ${selectedOwnerName}`}
            >
                <div className="space-y-4">
                    <div className="overflow-hidden border border-gray-100 rounded-xl">
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50 border-b border-gray-100">
                                <tr>
                                    <th className="py-3 px-4 text-right font-semibold text-gray-600">#</th>
                                    <th className="py-3 px-4 text-right font-semibold text-gray-600">{t('common.type')}</th>
                                    <th className="py-3 px-4 text-center font-semibold text-gray-600">{t('common.actions')}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {selectedCertificates.map((cert, index) => {
                                    const isPdf = cert.toLowerCase().endsWith('.pdf');
                                    return (
                                        <tr key={index} className="hover:bg-gray-50/50 transition-colors">
                                            <td className="py-3 px-4 text-gray-500 font-medium">{index + 1}</td>
                                            <td className="py-3 px-4">
                                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase ${isPdf ? 'bg-red-100 text-red-600' : 'bg-blue-100 text-blue-600'}`}>
                                                    {isPdf ? 'PDF' : 'IMAGE'}
                                                </span>
                                            </td>
                                            <td className="py-3 px-4">
                                                <div className="flex items-center justify-center gap-2">
                                                    <a
                                                        href={cert.startsWith('http') ? cert : `https://api.istanbulviillas.com/storage/${cert}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="p-1.5 hover:bg-gray-100 rounded-lg text-primary transition-colors"
                                                        title={t('common.view')}
                                                    >
                                                        <ExternalLink className="w-4 h-4" />
                                                    </a>
                                                    <a
                                                        href={cert.startsWith('http') ? cert : `https://api.istanbulviillas.com/storage/${cert}`}
                                                        download
                                                        className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500 transition-colors"
                                                        title={t('common.download')}
                                                    >
                                                        <Download className="w-4 h-4" />
                                                    </a>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    {selectedCertificates.length === 0 && (
                        <div className="text-center py-8 text-gray-400">
                            {t('common.no_data')}
                        </div>
                    )}
                </div>
            </Modal>

            <OwnerFormModal
                isOpen={isFormModalOpen}
                owner={ownerToEdit}
                onClose={() => setIsFormModalOpen(false)}
                onSuccess={() => {
                    fetchOwners();
                }}
            />
        </div>
    );
};
